import React, { useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";
import {
  Box,
  Button,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";

import DashboardWrapper from "../containers/DashboardWrapper";
import { getPlotData, setCurrentPage } from "../actions";
import LocalStorageHelper from "../utils/helper/localstorage_helper";
import Period from "../components/Filter/Period";
import PlotMapboxMap from "../components/Plot/PlotMapboxMap";

const DEFAULT_MAP_CENTER = [139.767125, 35.681236];
const DEFAULT_MAP_ZOOM = 11.7;
const sidePanelWidth = 360;

const TRUST_COLOR = {
  4: "#22c55e",
  3: "#eab308",
  2: "#fb923c",
  1: "#ef4444",
};

const ASSET_COLORS = [
  "#2563eb",
  "#7c3aed",
  "#0f766e",
  "#dc2626",
  "#ea580c",
  "#0284c7",
  "#16a34a",
  "#db2777",
];

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: "calc(100vh - 64px)",
    display: "flex",
    flexDirection: "column",
    background:
      "radial-gradient(circle at top left, rgba(41, 128, 185, 0.12), transparent 30%), linear-gradient(180deg, #f7fafc 0%, #eef4f8 100%)",
  },
  topBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    padding: theme.spacing(2),
    borderBottom: "1px solid rgba(15, 23, 42, 0.08)",
    background: "rgba(255,255,255,0.85)",
    backdropFilter: "blur(10px)",
  },
  topBarLeft: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    flexWrap: "nowrap",
    minWidth: 0,
  },
  topBarRight: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    marginLeft: "auto",
    minWidth: 0,
  },
  titleBlock: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
    marginRight: theme.spacing(1),
    flexShrink: 0,
  },
  title: {
    fontSize: 18,
    fontWeight: 700,
    color: "#0f172a",
  },
  subtitle: {
    fontSize: 12,
    color: "#64748b",
  },
  countText: {
    fontSize: 12,
    color: "#0f766e",
    border: "1px solid #0f766e",
    borderRadius: 999,
    padding: "4px 10px",
    whiteSpace: "nowrap",
    flexShrink: 0,
  },
  filter: {
    minWidth: 150,
    background: "#fff",
    flexShrink: 0,
  },
  clearButton: {
    flexShrink: 0,
    whiteSpace: "nowrap",
  },
  content: {
    flex: 1,
    display: "flex",
    overflow: "hidden",
  },
  mapArea: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    position: "relative",
  },
  mapShell: {
    flex: 1,
    position: "relative",
    margin: theme.spacing(2),
    borderRadius: 24,
    overflow: "hidden",
    background:
      "linear-gradient(135deg, rgba(15, 23, 42, 0.92), rgba(30, 41, 59, 0.88))",
    boxShadow: "0 18px 48px rgba(15, 23, 42, 0.16)",
  },
  legend: {
    position: "absolute",
    top: 16,
    right: 16,
    zIndex: 2,
    background: "rgba(255,255,255,0.94)",
    borderRadius: 16,
    padding: theme.spacing(1.5),
    minWidth: 190,
    boxShadow: "0 10px 30px rgba(15, 23, 42, 0.18)",
  },
  legendTitle: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.06em",
    color: "#64748b",
    marginBottom: theme.spacing(1),
    textTransform: "uppercase",
  },
  legendRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
    fontSize: 12,
    color: "#334155",
    marginBottom: 6,
  },
  legendRowLeft: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    minWidth: 0,
  },
  legendValue: {
    fontVariantNumeric: "tabular-nums",
    color: "#0f172a",
    flexShrink: 0,
  },
  dot: {
    width: 11,
    height: 11,
    borderRadius: "50%",
    flexShrink: 0,
  },
  pin: {
    position: "absolute",
    transform: "translate(-50%, -100%)",
    zIndex: 1,
    border: "none",
    background: "transparent",
    cursor: "pointer",
    color: "#fff",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 2,
  },
  pinBubble: {
    width: 40,
    height: 40,
    borderRadius: "50%",
    display: "grid",
    placeItems: "center",
    fontSize: 18,
    fontWeight: 700,
    boxShadow: "0 8px 18px rgba(0,0,0,0.28)",
    border: "3px solid rgba(255,255,255,0.95)",
  },
  pinStem: {
    width: 0,
    height: 0,
    borderLeft: "7px solid transparent",
    borderRight: "7px solid transparent",
    borderTop: "10px solid rgba(255,255,255,0.95)",
    marginTop: -4,
  },
  thumbWrap: {
    padding: theme.spacing(2, 2, 2),
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: theme.spacing(1.25),
    overflowY: "auto",
  },
  thumbCard: {
    border: "1px solid rgba(148, 163, 184, 0.28)",
    borderRadius: 14,
    overflow: "hidden",
    background: "#fff",
    cursor: "pointer",
    transition: "transform 120ms ease, box-shadow 120ms ease",
    "&:hover": {
      transform: "translateY(-1px)",
      boxShadow: "0 10px 18px rgba(15, 23, 42, 0.12)",
    },
  },
  thumbPreview: {
    height: 92,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 28,
    color: "#fff",
    position: "relative",
    overflow: "hidden",
    background: "linear-gradient(135deg, #0f172a, #334155)",
  },
  thumbImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
  },
  thumbOverlay: {
    position: "absolute",
    inset: 0,
    background: "linear-gradient(180deg, rgba(15,23,42,0.08), rgba(15,23,42,0.34))",
  },
  thumbAction: {
    position: "absolute",
    right: 8,
    top: 8,
    width: 26,
    height: 26,
    borderRadius: "50%",
    display: "grid",
    placeItems: "center",
    background: "rgba(255,255,255,0.9)",
    color: "#0f172a",
    boxShadow: "0 6px 16px rgba(15, 23, 42, 0.18)",
  },
  thumbMeta: {
    padding: theme.spacing(1),
    fontSize: 12,
    color: "#0f172a",
  },
  thumbSub: {
    fontSize: 11,
    color: "#64748b",
    marginTop: 2,
  },
  footerNote: {
    position: "absolute",
    left: 16,
    bottom: 16,
    zIndex: 2,
    background: "rgba(15, 23, 42, 0.66)",
    color: "#fff",
    borderRadius: 999,
    padding: "8px 12px",
    fontSize: 12,
  },
  sidePanel: {
    width: 0,
    flexShrink: 0,
    overflow: "hidden",
    borderLeft: "1px solid rgba(15, 23, 42, 0.08)",
    background: "#fff",
    transition: "width 180ms ease",
  },
  sidePanelOpen: {
    width: sidePanelWidth,
  },
  sidePanelInner: {
    width: sidePanelWidth,
    height: "100%",
    display: "flex",
    flexDirection: "column",
  },
  sidePanelHeader: {
    padding: theme.spacing(2),
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: theme.spacing(1),
  },
  sidePanelTitle: {
    fontSize: 16,
    fontWeight: 700,
    color: "#0f172a",
  },
  sidePanelSub: {
    fontSize: 12,
    color: "#64748b",
    marginTop: 4,
  },
  sidePanelClose: {
    border: "none",
    background: "transparent",
    padding: 0,
    lineHeight: 0,
    color: "#9ca3af",
    cursor: "pointer",
    flexShrink: 0,
    marginTop: -2,
    "&:hover": {
      color: "#6b7280",
    },
  },
  sidePanelTitleWrap: {
    minWidth: 0,
    flex: 1,
  },
  loadingBanner: {
    position: "absolute",
    left: 16,
    top: 16,
    zIndex: 2,
    background: "rgba(15, 23, 42, 0.82)",
    color: "#fff",
    borderRadius: 999,
    padding: "8px 12px",
    fontSize: 12,
  },
  emptyOverlay: {
    position: "absolute",
    inset: 0,
    display: "grid",
    placeItems: "center",
    background: "linear-gradient(135deg, rgba(15,23,42,0.82), rgba(30,41,59,0.72))",
    color: "#e2e8f0",
    textAlign: "center",
    padding: 24,
  },
  emptyOverlayInner: {
    maxWidth: 360,
    lineHeight: 1.8,
    fontSize: 13,
  },
}));

const trustLabel = (lv) => `Lv${lv}`;

const Plot = ({
  setCurrentPage,
  getPlotData,
  dateRange,
  minMaxDateRange,
  plotData,
  plotLoading,
  plotError,
}) => {
  const classes = useStyles();
  const hasLoadedInitialDataRef = useRef(false);
  const [selectedEvent, setSelectedEvent] = useState("all");
  const [selectedMuni, setSelectedMuni] = useState("all");
  const [activePlot, setActivePlot] = useState(null);
  const companyName = LocalStorageHelper.getCompany() || "";
  const currentAccessKey = localStorage.getItem("access_key");
  const providerLabel = currentAccessKey === "dea" ? "DEA" : (companyName ? companyName.toUpperCase() : "UNKNOWN");

  useEffect(() => {
    setCurrentPage(window.location.pathname);
  }, [setCurrentPage]);

  useEffect(() => {
    if (hasLoadedInitialDataRef.current) {
      return;
    }
    hasLoadedInitialDataRef.current = true;
    getPlotData({ dateRange });
  }, [dateRange, getPlotData]);

  const allPlots = useMemo(() => plotData || [], [plotData]);

  const eventOptions = useMemo(() => {
    const options = Array.from(
      new Map(
        allPlots.map((plot) => [plot.eventId || plot.event, { value: plot.eventId || plot.event, label: plot.event }])
      ).values()
    );
    options.sort((a, b) => a.label.localeCompare(b.label, "ja"));
    return [{ value: "all", label: "全て" }, ...options];
  }, [allPlots]);

  const muniOptions = useMemo(() => {
    const options = Array.from(
      new Map(
        allPlots.map((plot) => [
          plot.municipalityId || plot.municipality,
          { value: plot.municipalityId || plot.municipality, label: plot.municipality },
        ])
      ).values()
    );
    options.sort((a, b) => a.label.localeCompare(b.label, "ja"));
    return [{ value: "all", label: "全て" }, ...options];
  }, [allPlots]);

  useEffect(() => {
    if (selectedEvent !== "all" && !eventOptions.some((option) => option.value === selectedEvent)) {
      setSelectedEvent("all");
    }
  }, [eventOptions, selectedEvent]);

  useEffect(() => {
    if (selectedMuni !== "all" && !muniOptions.some((option) => option.value === selectedMuni)) {
      setSelectedMuni("all");
    }
  }, [muniOptions, selectedMuni]);

  const filteredPlots = useMemo(() => {
    return allPlots.filter((plot) => {
      if (selectedEvent !== "all" && plot.eventId !== selectedEvent && plot.event !== selectedEvent) {
        return false;
      }
      if (selectedMuni !== "all" && plot.municipalityId !== selectedMuni && plot.municipality !== selectedMuni) {
        return false;
      }
      return true;
    });
  }, [allPlots, selectedEvent, selectedMuni]);

  useEffect(() => {
    if (activePlot && !filteredPlots.some((plot) => plot.id === activePlot.id)) {
      setActivePlot(null);
    }
  }, [activePlot, filteredPlots]);

  const openViewer = (photoFileName) => {
    if (!photoFileName) {
      return;
    }
    window.open(`/plot-viewer/${encodeURIComponent(photoFileName)}`, "_blank", "noopener,noreferrer");
  };

  const isPanelOpen = Boolean(activePlot);
  const activePlotPhotos = activePlot?.photos || [];

  const trustLegend = useMemo(() => {
    const counts = filteredPlots.reduce(
      (acc, plot) => {
        acc[plot.lv] = (acc[plot.lv] || 0) + 1;
        return acc;
      },
      { 1: 0, 2: 0, 3: 0, 4: 0 }
    );
    const total = filteredPlots.length || 1;
    return [4, 3, 2, 1].map((lv) => ({
      lv,
      count: counts[lv] || 0,
      pct: ((counts[lv] || 0) / total) * 100,
    }));
  }, [filteredPlots]);

  const assetLegend = useMemo(() => {
    const uniqueTypes = Array.from(new Set(allPlots.map((plot) => plot.assetType))).sort((a, b) =>
      String(a).localeCompare(String(b), "ja")
    );
    const countsByType = allPlots.reduce((acc, plot) => {
      acc[plot.assetType] = (acc[plot.assetType] || 0) + 1;
      return acc;
    }, {});

    return uniqueTypes.map((assetType, index) => ({
      assetType,
      count: countsByType[assetType] || 0,
      color: ASSET_COLORS[index % ASSET_COLORS.length],
    }));
  }, [allPlots]);

  return (
    <DashboardWrapper padding={false}>
      <div className={classes.root}>
        <div className={classes.topBar}>
          <div className={classes.topBarLeft}>
            <div className={classes.titleBlock}>
              <div className={classes.title}>プロット</div>
              <div className={classes.subtitle}>STG1 データソース / {providerLabel}</div>
            </div>
            <FormControl variant="outlined" size="small" className={classes.filter}>
              <InputLabel>イベント</InputLabel>
              <Select
                value={selectedEvent}
                onChange={(e) => setSelectedEvent(e.target.value)}
                label="イベント"
              >
                {eventOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl variant="outlined" size="small" className={classes.filter}>
              <InputLabel>自治体</InputLabel>
              <Select
                value={selectedMuni}
                onChange={(e) => setSelectedMuni(e.target.value)}
                label="自治体"
              >
                {muniOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <div className={classes.countText}>
              表示 {filteredPlots.length} 件 / 全 {allPlots.length} 件
            </div>
            {(selectedEvent !== "all" || selectedMuni !== "all") && (
              <Button
                className={classes.clearButton}
                onClick={() => {
                  setSelectedEvent("all");
                  setSelectedMuni("all");
                }}
              >
                クリア
              </Button>
            )}
          </div>
          <div className={classes.topBarRight}>
            <Period
              companyName={companyName}
              plotMode={true}
              dateRange={dateRange}
              minMaxDateRange={minMaxDateRange}
              onApply={(range) => getPlotData({ dateRange: range })}
            />
          </div>
        </div>

        <div className={classes.content}>
          <div className={classes.mapArea}>
            <div className={classes.mapShell}>
              <PlotMapboxMap
                plots={filteredPlots}
                activePlotId={activePlot?.id}
                onSelectPlot={setActivePlot}
                panelOpen={isPanelOpen}
                initialCenter={DEFAULT_MAP_CENTER}
                initialZoom={DEFAULT_MAP_ZOOM}
              />

              {plotLoading && <div className={classes.loadingBanner}>STG1 データを読み込み中です。</div>}
              {plotError && !plotLoading && <div className={classes.loadingBanner}>{plotError}</div>}
              {!plotLoading && !plotError && filteredPlots.length === 0 && (
                <div className={classes.emptyOverlay}>
                  <div className={classes.emptyOverlayInner}>
                    条件に一致するプロットがありません。
                    <br />
                    日付・イベント・自治体の条件を見直してください。
                  </div>
                </div>
              )}

              <div className={classes.legend}>
                <div className={classes.legendTitle}>信頼度</div>
                {trustLegend.map((item) => (
                  <div className={classes.legendRow} key={item.lv}>
                    <div className={classes.legendRowLeft}>
                      <span className={classes.dot} style={{ background: TRUST_COLOR[item.lv] }} />
                      <span>{trustLabel(item.lv)}</span>
                    </div>
                    <span className={classes.legendValue}>
                      {item.count} ({item.pct.toFixed(1)}%)
                    </span>
                  </div>
                ))}

                <Divider style={{ margin: "8px 0" }} />

                <div className={classes.legendTitle}>アセット種別</div>
                {assetLegend.map((item) => (
                  <div className={classes.legendRow} key={item.assetType}>
                    <div className={classes.legendRowLeft}>
                      <span className={classes.dot} style={{ background: item.color }} />
                      <span>{item.assetType}</span>
                    </div>
                    <span className={classes.legendValue}>{item.count}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className={`${classes.sidePanel} ${isPanelOpen ? classes.sidePanelOpen : ""}`}>
            {isPanelOpen && activePlot && (
              <div className={classes.sidePanelInner}>
                <div className={classes.sidePanelHeader}>
                  <div className={classes.sidePanelTitleWrap}>
                    <Typography className={classes.sidePanelTitle}>{activePlot.name}</Typography>
                    <Typography className={classes.sidePanelSub}>
                      {activePlot.id} / {activePlot.municipality} / {activePlot.assetType} / {trustLabel(activePlot.lv)}
                    </Typography>
                  </div>
                  <button
                    type="button"
                    className={classes.sidePanelClose}
                    onClick={() => setActivePlot(null)}
                    aria-label="閉じる"
                  >
                    <CloseIcon />
                  </button>
                </div>
                <Divider />
                <Box className={classes.thumbWrap}>
                  {activePlotPhotos.length > 0 ? (
                    activePlotPhotos.map((photo) => (
                      <div
                        key={photo.id}
                        className={classes.thumbCard}
                        onClick={() => openViewer(photo.fileName)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            openViewer(photo.fileName);
                          }
                        }}
                      >
                        <div
                          className={classes.thumbPreview}
                          style={{
                            backgroundImage: photo.thumbUrl
                              ? `linear-gradient(180deg, rgba(15,23,42,0.08), rgba(15,23,42,0.34)), url(${photo.thumbUrl})`
                              : "linear-gradient(135deg, #0f172a, #334155)",
                            backgroundSize: "cover",
                            backgroundPosition: "center",
                          }}
                        >
                          <div className={classes.thumbOverlay} />
                          <div className={classes.thumbAction}>
                            <OpenInNewIcon style={{ fontSize: 14 }} />
                          </div>
                        </div>
                        <div className={classes.thumbMeta}>
                          <div>{photo.label}</div>
                          <div className={classes.thumbSub}>{photo.capturedAt || " "}</div>
                          <div className={classes.thumbSub}>
                            {photo.fileName || "file_name not found"}
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div style={{ padding: 16, fontSize: 13, color: "#64748b" }}>
                      このアセットにはサムネイルがありません。
                    </div>
                  )}
                </Box>
              </div>
            )}
          </div>
        </div>
      </div>
    </DashboardWrapper>
  );
};

Plot.propTypes = {
  setCurrentPage: PropTypes.func.isRequired,
  getPlotData: PropTypes.func.isRequired,
  dateRange: PropTypes.array,
  minMaxDateRange: PropTypes.array,
  plotData: PropTypes.array,
  plotLoading: PropTypes.bool,
  plotError: PropTypes.string,
};

const mapStateToProps = (state) => ({
  dateRange: state.app.dateRange,
  minMaxDateRange: state.app.minMaxDateRange,
  plotData: state.plot.plotData,
  plotLoading: state.plot.plotLoading,
  plotError: state.plot.plotError,
});

const mapDispatchToProps = (dispatch) => ({
  setCurrentPage: (param) => dispatch(setCurrentPage(param)),
  getPlotData: (param) => dispatch(getPlotData(param)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Plot);
